// CSR-857: Add / Edit Contact modal for the Insurance Company detail
// page. PRD fields: Full Name (required), Email, Phone, Role /
// Relationship, Primary Contact (boolean). Backend (CSR-820) enforces
// the single-isPrimary invariant via `demoteOtherPrimaryContacts`, so
// the client can freely toggle without manual reconciliation.

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCreateInsuranceCompanyContact,
  useUpdateInsuranceCompanyContact,
  type InsertInsuranceCompanyContactClient,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import type { InsuranceCompanyContact } from "@shared/schema";

interface FormState {
  fullName: string;
  email: string;
  phone: string;
  role: string;
  isPrimary: boolean;
}

const EMPTY_FORM: FormState = {
  fullName: "",
  email: "",
  phone: "",
  role: "",
  isPrimary: false,
};

const formSchema = z.object({
  fullName: z.string().trim().min(1, "Full name is required"),
  email: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || z.string().email().safeParse(v).success, {
      message: "Must be a valid email",
    }),
  phone: z.string().trim().optional(),
  role: z.string().trim().optional(),
  isPrimary: z.boolean(),
});

type FormErrors = Partial<Record<keyof FormState, string>>;

function toFormState(contact: InsuranceCompanyContact): FormState {
  return {
    fullName: contact.fullName ?? "",
    email: contact.email ?? "",
    phone: contact.phone ?? "",
    role: contact.role ?? "",
    isPrimary: contact.isPrimary,
  };
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

interface ContactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  companyId: string;
  contact: InsuranceCompanyContact | null;
}

export function ContactFormDialog({
  open,
  onOpenChange,
  mode,
  companyId,
  contact,
}: ContactFormDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const { toast } = useToast();
  const createMutation = useCreateInsuranceCompanyContact();
  const updateMutation = useUpdateInsuranceCompanyContact();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when the dialog opens or the edit target changes.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(mode === "edit" && contact ? toFormState(contact) : EMPTY_FORM);
  }, [open, mode, contact]);

  const handleChange = <K extends keyof FormState>(
    key: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = formSchema.safeParse(form);
    if (!result.success) {
      const next: FormErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof FormState | undefined;
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }

    const payload: InsertInsuranceCompanyContactClient = {
      fullName: result.data.fullName,
      email: emptyToNull(result.data.email ?? ""),
      phone: emptyToNull(result.data.phone ?? ""),
      role: emptyToNull(result.data.role ?? ""),
      isPrimary: result.data.isPrimary,
    };

    if (mode === "create") {
      createMutation.mutate(
        { companyId, data: payload },
        {
          onSuccess: () => {
            toast({ title: "Contact added" });
            onOpenChange(false);
          },
          onError: (err) => {
            toast({
              title: "Failed to add contact",
              description: err instanceof Error ? err.message : undefined,
              variant: "destructive",
            });
          },
        },
      );
    } else if (contact) {
      updateMutation.mutate(
        { id: contact.id, companyId, data: payload },
        {
          onSuccess: () => {
            toast({ title: "Contact updated" });
            onOpenChange(false);
          },
          onError: (err) => {
            toast({
              title: "Failed to update contact",
              description: err instanceof Error ? err.message : undefined,
              variant: "destructive",
            });
          },
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="dialog-contact-form"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add Contact" : "Edit Contact"}
          </DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Add a new contact for this insurance company."
              : "Update this contact's details."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="contact-fullName">
              Full Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="contact-fullName"
              value={form.fullName}
              onChange={(e) => handleChange("fullName", e.target.value)}
              data-testid="input-contact-fullName"
              autoFocus
            />
            {errors.fullName && (
              <p
                className="text-xs text-destructive"
                data-testid="error-contact-fullName"
              >
                {errors.fullName}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="contact-email">Email</Label>
            {/* type="text" rather than "email" so HTML5 validation
                doesn't pre-empt our Zod check (which produces a
                consistent inline error). */}
            <Input
              id="contact-email"
              type="text"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              data-testid="input-contact-email"
            />
            {errors.email && (
              <p
                className="text-xs text-destructive"
                data-testid="error-contact-email"
              >
                {errors.email}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="contact-phone">Phone</Label>
            <Input
              id="contact-phone"
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              data-testid="input-contact-phone"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="contact-role">Role / Relationship</Label>
            <Input
              id="contact-role"
              value={form.role}
              onChange={(e) => handleChange("role", e.target.value)}
              data-testid="input-contact-role"
              placeholder="e.g. Account Manager"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="contact-isPrimary"
              checked={form.isPrimary}
              onCheckedChange={(checked) =>
                handleChange("isPrimary", checked === true)
              }
              data-testid="input-contact-isPrimary"
            />
            <Label
              htmlFor="contact-isPrimary"
              className="font-normal cursor-pointer"
            >
              Primary contact
            </Label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              data-testid="button-submit-contact"
            >
              {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {mode === "create" ? "Add Contact" : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
